import { create } from 'zustand';
import { TabSong } from '../types/index.js';
import type { AlphaTabApi } from '@coderline/alphatab';

export interface TrackMixerState {
  trackIndex: number;
  name: string;
  volume: number; // 0 to 1
  isMuted: boolean;
  isSolo: boolean;
}

interface TabPlayerState {
  currentTab: TabSong | null;
  isPlaying: boolean;
  playbackSpeed: number; // 0.1 to 1.0 (10% to 100%)
  bpm: number;
  activePlaybackSeconds: number;
  currentTimeMs: number;
  durationMs: number;
  currentMeasure: number;
  totalMeasures: number;
  isLooping: boolean;
  loopStartMeasure: number | null;
  loopEndMeasure: number | null;
  /** Set by the Dashboard Continue card: the next tab opened with this id keeps the loop the card just applied (FRE-158). */
  restoreForTabId: string | null;
  countInBars: number; // 0, 1, 2
  metronomeActive: boolean;
  activeTrackIndex: number;
  tracks: TrackMixerState[];
  api: AlphaTabApi | null;

  // Actions
  setCurrentTab: (tab: TabSong | null) => void;
  setRestoreForTabId: (tabId: string | null) => void;
  setIsPlaying: (playing: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  setBpm: (bpm: number) => void;
  incrementActivePlaybackTime: (deltaSeconds: number) => void;
  resetActivePlaybackTime: () => void;
  setLooping: (isLooping: boolean) => void;
  setLoopRange: (start: number | null, end: number | null) => void;
  setCountIn: (bars: number) => void;
  setMetronomeActive: (active: boolean) => void;
  setActiveTrackIndex: (index: number) => void;
  setTracks: (tracks: TrackMixerState[]) => void;
  updateTrackMixer: (trackIndex: number, update: Partial<TrackMixerState>) => void;
  setPlaybackPosition: (timeMs: number, durationMs: number, measure: number, totalMeasures: number) => void;
  setApiInstance: (api: AlphaTabApi | null) => void;
}

export const useTabStore = create<TabPlayerState>((set) => ({
  currentTab: null,
  isPlaying: false,
  playbackSpeed: 1.0,
  bpm: 120,
  activePlaybackSeconds: 0,
  currentTimeMs: 0,
  durationMs: 0,
  currentMeasure: 1,
  totalMeasures: 1,
  isLooping: false,
  loopStartMeasure: null,
  loopEndMeasure: null,
  restoreForTabId: null,
  countInBars: 1,
  metronomeActive: false,
  activeTrackIndex: 0,
  tracks: [],
  api: null,

  setCurrentTab: (currentTab) =>
    set((state) => {
      // A loop belongs to the tab it was set on (FRE-158): opening a different tab starts without one, unless the
      // Continue card just restored this tab's loop.
      const keepLoop =
        !!currentTab && (currentTab.id === state.currentTab?.id || currentTab.id === state.restoreForTabId);
      return {
      currentTab,
      restoreForTabId: null,
      ...(keepLoop ? {} : { isLooping: false, loopStartMeasure: null, loopEndMeasure: null }),
      activePlaybackSeconds: 0,
      isPlaying: false,
      currentTimeMs: 0,
      durationMs: 0,
      currentMeasure: 1,
      totalMeasures: 1
      };
    }),

  setRestoreForTabId: (restoreForTabId) => set({ restoreForTabId }),
  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setPlaybackSpeed: (speed) => set({ playbackSpeed: Math.max(0.1, Math.min(1.0, speed)) }),
  setBpm: (bpm) => set({ bpm }),
  incrementActivePlaybackTime: (delta) =>
    set((state) => ({ activePlaybackSeconds: state.activePlaybackSeconds + delta })),
  resetActivePlaybackTime: () => set({ activePlaybackSeconds: 0 }),
  setLooping: (isLooping) => set({ isLooping }),
  setLoopRange: (start, end) => set({ loopStartMeasure: start, loopEndMeasure: end }),
  setCountIn: (countInBars) => set({ countInBars }),
  setMetronomeActive: (metronomeActive) => set({ metronomeActive }),
  setActiveTrackIndex: (activeTrackIndex) => set({ activeTrackIndex }),
  setTracks: (tracks) => set({ tracks }),
  updateTrackMixer: (trackIndex, update) =>
    set((state) => ({
      tracks: state.tracks.map((t) => (t.trackIndex === trackIndex ? { ...t, ...update } : t))
    })),
  setPlaybackPosition: (currentTimeMs, durationMs, currentMeasure, totalMeasures) =>
    set({
      currentTimeMs,
      durationMs: durationMs || 0,
      currentMeasure: currentMeasure || 1,
      totalMeasures: totalMeasures || 1
    }),
  setApiInstance: (api) => set({ api })
}));

if (import.meta.env?.DEV && typeof window !== 'undefined') {
  (window as any).__tabStore = useTabStore;
}
